import { useCallback } from 'react';

import { USER_ANNOTATIONS_ORIGIN } from 'src/data/performance';
import * as events from 'src/lib/events';
import * as gtm from 'src/lib/gtm';
import { useTrackGtmEvent } from 'src/services';
import { PAGES, SUB_PAGES, useTrackingEvent } from 'src/services/useTrackingEvent';

import { useGetProjectAttributes } from '../../../services';
import { useLocalActions } from '../store';

export const useGenerationFormEvents = () => {
  const localActions = useLocalActions();
  const trackingEvent = useTrackingEvent();
  const getProjectAttributes = useGetProjectAttributes();
  const trackGtmEvent = useTrackGtmEvent();

  const trackClickedTargetAudience = useCallback(() => {
    const attributes = {
      ...getProjectAttributes(),
      page: PAGES.editor,
      subPage: SUB_PAGES.generationPanel,
    };

    trackingEvent(events.DOCUMENT.clickedTargetAudience, attributes);
  }, [trackingEvent, getProjectAttributes]);

  const trackAddedTalkingPoint = useCallback(
    (origin = null) => {
      const generationTool = localActions.getGenerationTool();

      const dimensions = { generationTool, origin: origin || USER_ANNOTATIONS_ORIGIN };

      const attributes = {
        ...getProjectAttributes(),
        ...dimensions,
        page: PAGES.editor,
        subPage: SUB_PAGES.generationPanel,
      };

      trackingEvent(events.DOCUMENT.addedTalkingPoint, attributes);
      trackGtmEvent(gtm.EVENTS.addedTalkingPoints, attributes);
    },
    [localActions, trackingEvent, getProjectAttributes, trackGtmEvent]
  );

  const trackSaveInstructions = useCallback(
    (options) => {
      trackingEvent(events.DOCUMENT.freestyleSaveInstructions, {
        ...getProjectAttributes(),
        status: options?.updated ? 'updated' : 'created',
        generationTool: localActions.getGenerationTool(),
        page: PAGES.editor,
        subPage: SUB_PAGES.generationPanel,
      });
    },
    [getProjectAttributes, localActions, trackingEvent]
  );

  const trackSuggestedPromptClicked = useCallback(
    (type) => {
      const attributes = {
        ...getProjectAttributes(),
        generationTool: localActions.getGenerationTool(),
        page: PAGES.editor,
        subPage: SUB_PAGES.generationPanel,
        type,
      };
      trackingEvent(events.DOCUMENT.freestyleSuggestPromptClicked, attributes);
    },
    [trackingEvent, getProjectAttributes, localActions]
  );

  const trackClickedAnnotation = useCallback(
    (annotation) => {
      const attributes = {
        ...getProjectAttributes(),
        annotation: annotation.content,
        rating: annotation.rating,
        page: PAGES.editor,
        subPage: SUB_PAGES.generationPanel,
      };
      trackingEvent(events.PROJECT.talkingPointClicked, attributes);
    },
    [trackingEvent, getProjectAttributes]
  );

  return {
    trackClickedTargetAudience,
    trackAddedTalkingPoint,
    trackSaveInstructions,
    trackSuggestedPromptClicked,
    trackClickedAnnotation,
  };
};
